'use client';

import { useState, type FormEvent } from 'react';
import { ArrowRight, Check } from 'lucide-react';

type Status = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'success' } | { kind: 'error'; message: string };

export function SubscribeForm({
  variant = 'card',
  ctaLabel = 'Subscribe',
  showFirstName = true,
}: {
  variant?: 'card' | 'inline';
  ctaLabel?: string;
  showFirstName?: boolean;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (status.kind === 'submitting') return;

    setStatus({ kind: 'submitting' });

    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, firstName, website }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };

      if (!res.ok) {
        setStatus({
          kind: 'error',
          message: data.error || 'Something went wrong. Please try again.',
        });
        return;
      }

      setStatus({ kind: 'success' });
      setEmail('');
      setFirstName('');
    } catch {
      setStatus({
        kind: 'error',
        message: 'Network error. Please try again.',
      });
    }
  }

  if (status.kind === 'success') {
    return (
      <div
        className={
          variant === 'card'
            ? 'bg-primary/5 border border-primary/20 rounded-2xl p-6 flex items-start gap-3'
            : 'flex items-center gap-2 text-sm text-primary'
        }
      >
        <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">You&apos;re on the list.</div>
          <p className="text-sm text-muted-foreground mt-1">
            Watch your inbox — your first issue arrives within a few days.
          </p>
        </div>
      </div>
    );
  }

  const submitting = status.kind === 'submitting';

  if (variant === 'inline') {
    return (
      <form onSubmit={onSubmit} className="space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="flex-1 rounded-lg border border-border/60 bg-white dark:bg-card px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Email address"
            disabled={submitting}
          />
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="hidden"
            aria-hidden="true"
          />
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all disabled:opacity-60 disabled:translate-y-0"
          >
            {submitting ? 'Subscribing…' : ctaLabel}
            {!submitting && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
        {status.kind === 'error' && (
          <p className="text-xs text-red-500">{status.message}</p>
        )}
      </form>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white dark:bg-card border border-border/60 rounded-2xl p-6 space-y-4"
    >
      <div className="space-y-3">
        {showFirstName && (
          <div>
            <label htmlFor="newsletter-first-name" className="block text-xs font-medium text-muted-foreground mb-1">
              First name <span className="text-muted-foreground/60">(optional)</span>
            </label>
            <input
              id="newsletter-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Jamie"
              className="w-full rounded-lg border border-border/60 bg-white dark:bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              maxLength={80}
              disabled={submitting}
            />
          </div>
        )}
        <div>
          <label htmlFor="newsletter-email" className="block text-xs font-medium text-muted-foreground mb-1">
            Email address
          </label>
          <input
            id="newsletter-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="w-full rounded-lg border border-border/60 bg-white dark:bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={submitting}
          />
        </div>
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="hidden"
          aria-hidden="true"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-5 py-3 text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 transition-all disabled:opacity-60 disabled:translate-y-0"
      >
        {submitting ? 'Subscribing…' : ctaLabel}
        {!submitting && <ArrowRight className="w-4 h-4" />}
      </button>
      {status.kind === 'error' && (
        <p className="text-xs text-red-500 text-center">{status.message}</p>
      )}
      <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
        One issue per week. Unsubscribe any time. We never share your email.
      </p>
    </form>
  );
}
